import React, { useState, useMemo, useEffect } from "react";
import { ChevronDown, ChevronUp, RotateCcw, ArrowLeft } from "lucide-react";
import defaultImg from "../../assets/default.jpeg";
import {
  useGetEmployeeExpenses,
  useGetExpenseByCategory,
  useGetCategorySummary,
  useGetExpenses // ✅ Import the new hook
} from "../../hooks/useExpenceApi";
import ExpensesList from "./ExpensesList"; // ✅ Import ExpensesList component

const getCategoryBadgeClass = (index) => {
  const colors = [
    "bg-blue-100 text-blue-800",
    "bg-purple-100 text-purple-800",
    "bg-pink-100 text-pink-800",
    "bg-indigo-100 text-indigo-800",
    "bg-teal-100 text-teal-800",
  ];
  return colors[index % colors.length];
};

export const ExpenseReportList = () => {
  const [expandedId, setExpandedId] = useState(null);
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);

  // ✅ NEW: Selected employee state for expense details
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  // const [selectedEmployeeName, setSelectedEmployeeName] = useState("");

  // ✅ Tab state - "employee" or "category"
  const [activeTab, setActiveTab] = useState("employee");

  // 🔹 EMPLOYEE LIST FILTERS (Original)
  const [searchFilter, setSearchFilter] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // 🔹 CATEGORY VIEW FILTERS (For employee list in category)
  const [categorySearchFilter, setCategorySearchFilter] = useState("");
  const [categoryDebouncedSearch, setCategoryDebouncedSearch] = useState("");
  const [categoryFromDate, setCategoryFromDate] = useState("");
  const [categoryToDate, setCategoryToDate] = useState("");
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryLimit] = useState(10);

  // 🔹 CATEGORY SUMMARY FILTERS (New for Category Summary tab)
  const [categorySummarySearch, setCategorySummarySearch] = useState("");
  const [categorySummaryDebouncedSearch, setCategorySummaryDebouncedSearch] = useState("");
  const [categorySummaryFromDate, setCategorySummaryFromDate] = useState("");
  const [categorySummaryToDate, setCategorySummaryToDate] = useState("");
  const [categorySummaryPage, setCategorySummaryPage] = useState(1);
  const [categorySummaryLimit] = useState(10);

  // ✅ NEW: Expense details filters
  const [expensePage, setExpensePage] = useState(1);
  const [expenseLimit] = useState(10);

  // ✅ Debounce logic for employee list
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchFilter);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchFilter]);

  // ✅ Debounce logic for category employee view
  useEffect(() => {
    const handler = setTimeout(() => {
      setCategoryDebouncedSearch(categorySearchFilter);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [categorySearchFilter]);

  // ✅ Debounce logic for category summary
  useEffect(() => {
    const handler = setTimeout(() => {
      setCategorySummaryDebouncedSearch(categorySummarySearch);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [categorySummarySearch]);

  // 🔹 Build filters for Employee List API
  const adminFilters = useMemo(() => {
    const f = { page, limit };

    if (debouncedSearch) f.search = debouncedSearch.trim();
    if (fromDate)
      f.startDate = Math.floor(new Date(fromDate + "T00:00:00").getTime() / 1000);
    if (toDate)
      f.endDate = Math.floor(new Date(toDate + "T23:59:59").getTime() / 1000);

    return f;
  }, [page, limit, debouncedSearch, fromDate, toDate]);

  // 🔹 Build filters for Category API (Separate filters)
  const categoryFilters = useMemo(() => {
    const f = { page: categoryPage, limit: categoryLimit };

    if (expandedCategoryId) f.categoryId = expandedCategoryId;
    if (categoryDebouncedSearch) f.search = categoryDebouncedSearch.trim();
    if (categoryFromDate)
      f.startDate = Math.floor(new Date(categoryFromDate + "T00:00:00").getTime() / 1000);
    if (categoryToDate)
      f.endDate = Math.floor(new Date(categoryToDate + "T23:59:59").getTime() / 1000);

    return f;
  }, [expandedCategoryId, categoryPage, categoryLimit, categoryDebouncedSearch, categoryFromDate, categoryToDate]);

  // 🔹 Build filters for Category Summary API
  const categorySummaryFilters = useMemo(() => {
    const f = { page: categorySummaryPage, limit: categorySummaryLimit };

    if (categorySummaryDebouncedSearch) f.search = categorySummaryDebouncedSearch.trim();
    if (categorySummaryFromDate)
      f.startDate = Math.floor(new Date(categorySummaryFromDate + "T00:00:00").getTime() / 1000);
    if (categorySummaryToDate)
      f.endDate = Math.floor(new Date(categorySummaryToDate + "T23:59:59").getTime() / 1000);

    return f;
  }, [categorySummaryPage, categorySummaryLimit, categorySummaryDebouncedSearch, categorySummaryFromDate, categorySummaryToDate]);

  // ✅ NEW: Build filters for Expense Details API
  const expenseFilters = useMemo(() => {
    const f = {
      employeeId: selectedEmployeeId,
      page: expensePage,
      limit: expenseLimit,
    };

    // Include category filter if viewing from category view
    if (expandedCategoryId) {
      f.categoryId = expandedCategoryId;
    }

    return f;
  }, [selectedEmployeeId, expensePage, expenseLimit, expandedCategoryId]);

  // 🔹 Fetch employee expenses
  const { data: employeeExpense, isLoading } = useGetEmployeeExpenses(adminFilters);
  const expenses = employeeExpense?.expenses || [];

  // 🔹 Fetch category expense details
  const { data: categoryExpenseData, isLoading: isCategoryLoading } = useGetExpenseByCategory(
    categoryFilters,
    { enabled: !!expandedCategoryId && !selectedEmployeeId } // ✅ Disable when viewing employee expenses
  );

  const categoryEmployees = useMemo(() => {
    return categoryExpenseData?.data || categoryExpenseData?.employees || [];
  }, [categoryExpenseData]);

  // 🔹 Fetch category summary
  const { data: categorySummaryData, isLoading: isCategorySummaryLoading } = useGetCategorySummary(
    categorySummaryFilters,
    { enabled: activeTab === "category" && !expandedCategoryId && !selectedEmployeeId }
  );
  const categoryList = categorySummaryData?.categories || [];

  // ✅ NEW: Fetch expense details for selected employee
  const { data: expenseDetailsData, isLoading: isExpenseDetailsLoading } = useGetExpenses(
    expenseFilters,
    { enabled: !!selectedEmployeeId }
  );

  const expenseDetails = expenseDetailsData?.expenses || [];
  const expenseTotalPages = expenseDetailsData?.pages || 1;

  // 🔹 Debug logs
  useEffect(() => {
    if (expandedCategoryId) {
      // console.log('🔍 Category Expense Data:', categoryExpenseData);
      // console.log('👥 Category Employees:', categoryEmployees);
      // console.log('📊 Employees Length:', categoryEmployees.length);
    }
  }, [categoryExpenseData, expandedCategoryId]);

  useEffect(() => {
    if (expandedCategoryId && categoryEmployees.length > 0 && !selectedEmployeeId) {
      // Auto-select first employee in the expanded category to show their expenses
      setSelectedEmployeeId(categoryEmployees[0].employeeId);
      setExpensePage(1);
    }
  }, [expandedCategoryId, categoryEmployees, selectedEmployeeId]);

  // Reset employee list filters
  const handleResetFilters = () => {
    setSearchFilter("");
    setDebouncedSearch("");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  // ✅ Reset category employee filters
  const handleResetCategoryFilters = () => {
    setCategorySearchFilter("");
    setCategoryDebouncedSearch("");
    setCategoryFromDate("");
    setCategoryToDate("");
    setCategoryPage(1);
  };

  // ✅ Reset category summary filters
  const handleResetCategorySummaryFilters = () => {
    setCategorySummarySearch("");
    setCategorySummaryDebouncedSearch("");
    setCategorySummaryFromDate("");
    setCategorySummaryToDate("");
    setCategorySummaryPage(1);
  };

  // ✅ Handle category click
  const handleCategoryClick = (categoryId, categoryName) => {
    console.log('🎯 Category clicked:', categoryId, categoryName);
    setExpandedCategoryId(categoryId);
    setSelectedEmployeeId(null); // ✅ Reset employee selection
    handleResetCategoryFilters();
  };

  // ✅ NEW: Handle employee click from category view
  const handleEmployeeClick = (employeeId, employeeName) => {
    // console.log('👤 Employee clicked:', employeeId, employeeName);
    setSelectedEmployeeId(employeeId);
    // setSelectedEmployeeName(employeeName);
    setExpensePage(1); // Reset to first page
  };

  // ✅ Go back to category employee list
  const handleBackToEmployeeList = () => {
    setSelectedEmployeeId(null);
    setExpensePage(1);
    setCategoryPage(1);
    setCategorySearchFilter("");
    setCategoryDebouncedSearch("");
    setCategoryFromDate("");
    setCategoryToDate("");
    setExpandedCategoryId(null); // RESET expandedCategoryId to go back fully
  };



  // ✅ Go back to main list
  const handleBackToList = () => {
    setSelectedEmployeeId(null);
    setExpensePage(1);
    setCategoryPage(1); // Reset to first page of category employees
    setCategorySearchFilter("");
    setCategoryDebouncedSearch("");
    setCategoryFromDate("");
    setCategoryToDate("");
  };

  // ✅ Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setExpandedCategoryId(null);
    setSelectedEmployeeId(null);
  };

  // ✅ Handle expense page change
  const handleExpensePageChange = (newPage) => {
    setExpensePage(newPage);
  };

  if (isLoading) return <p></p>;
  if (!expenses) return null;

  if (expenses.length === 0 && !expandedCategoryId && activeTab === "employee") {
    return (
      <div>
        {/* Tabs */}
        <div className="flex w-full pb-4 gap-2">
          <button
            onClick={() => handleTabChange("employee")}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === "employee"
              ? "bg-[#2C4E86] text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            Employee Wise
          </button>
          <button
            onClick={() => handleTabChange("category")}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === "category"
              ? "bg-[#2C4E86] text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            Category Wise
          </button>
        </div>

        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
              <label className="text-sm text-gray-600 mb-1 font-semibold">Search Employee</label>
              <input
                type="text"
                placeholder="Search by employee name"
                value={searchFilter}
                onChange={(e) => {
                  setSearchFilter(e.target.value);
                  setPage(1);
                }}
                className="border px-3 py-2 rounded-md"
              />
              {searchFilter !== debouncedSearch && (
                <span className="text-xs text-gray-500 mt-1">Searching...</span>
              )}
            </div>

            <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
              <label className="text-sm text-gray-600 mb-1 font-semibold">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
                className="border px-3 py-2 rounded-md"
              />
            </div>

            <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
              <label className="text-sm text-gray-600 mb-1 font-semibold">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
                className="border px-3 py-2 rounded-md"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm text-transparent mb-1 font-semibold select-none">Reset</label>
              <button
                onClick={handleResetFilters}
                className="border px-2 py-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-gray-700"
                title="Clear all filters"
              >
                <RotateCcw size={17} />
                <span className="text-xs">Reset</span>
              </button>
            </div>
          </div>
        </div>
        <p className="text-gray-500 mt-4 text-center">No expense reports found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ✅ CONDITIONAL RENDERING */}
      {selectedEmployeeId ? (
        // Expense details view
        <div>
          <button
            onClick={handleBackToEmployeeList}
            className="mb-4 flex items-center gap-2 text-[#2C4E86] hover:text-blue-900 font-medium transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Employee List
          </button>
          <ExpensesList
            expenses={expenseDetails}
            isLoading={isExpenseDetailsLoading}
            statusFilter={["pending", "approve", "reject"]}
            paginationData={{ page: expensePage, pages: expenseTotalPages }}
            onPageChange={handleExpensePageChange}
          />
        </div>
      ) : expandedCategoryId ? (
        // Category employee list view (same as when clicking on an employee in category view)
        <div>
          <button
            onClick={handleBackToList}
            className="mb-4 flex items-center gap-2 text-[#2C4E86] hover:text-blue-900 font-medium transition-colors"
          >
            <ArrowLeft size={18} />
            Back to {activeTab === "employee" ? "Employee" : "Category"} List
          </button>
          <div className="mb-6">
            {/* Filters for category employee list */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                <label className="text-sm text-gray-600 mb-1 font-semibold">Search Employee</label>
                <input
                  type="text"
                  placeholder="Search by employee name"
                  value={categorySearchFilter}
                  onChange={(e) => {
                    setCategorySearchFilter(e.target.value);
                    setCategoryPage(1);
                  }}
                  className="border px-3 py-2 rounded-md"
                />
                {categorySearchFilter !== categoryDebouncedSearch && (
                  <span className="text-xs text-gray-500 mt-1">Searching...</span>
                )}
              </div>
              <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                <label className="text-sm text-gray-600 mb-1 font-semibold">From Date</label>
                <input
                  type="date"
                  value={categoryFromDate}
                  onChange={(e) => {
                    setCategoryFromDate(e.target.value);
                    setCategoryPage(1);
                  }}
                  className="border px-3 py-2 rounded-md"
                />
              </div>
              <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                <label className="text-sm text-gray-600 mb-1 font-semibold">To Date</label>
                <input
                  type="date"
                  value={categoryToDate}
                  onChange={(e) => {
                    setCategoryToDate(e.target.value);
                    setCategoryPage(1);
                  }}
                  className="border px-3 py-2 rounded-md"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm text-transparent mb-1 font-semibold select-none">Reset</label>
                <button
                  onClick={handleResetCategoryFilters}
                  className="border px-2 py-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-gray-700"
                  title="Clear all filters"
                >
                  <RotateCcw size={17} />
                  <span className="text-xs">Reset</span>
                </button>
              </div>
            </div>
          </div>
          {isCategoryLoading || (!selectedEmployeeId && categoryEmployees.length > 0) ? (
            <p>Loading expenses...</p> // or a spinner component
          ) : categoryEmployees && categoryEmployees.length > 0 ? (
            <div>
              {categoryEmployees.map((employee) => (
                <div
                  key={employee.employeeId}
                  onClick={() => handleEmployeeClick(employee.employeeId, employee.employeeName)}
                  className="bg-gray-50 shadow rounded-lg overflow-hidden transition-all duration-200 hover:shadow-md mb-3 cursor-pointer"
                >
                  <div className="grid grid-cols-3 items-center px-4 py-3 gap-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={defaultImg}
                        alt={employee.employeeName}
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                      />
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-500">Employee</label>
                        <p className="font-medium text-gray-800">{employee.employeeName}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-xs font-medium text-gray-500">Expenses</span>
                      <p className="text-base text-gray-800 font-semibold">
                        {employee.expenseCount} {employee.expenseCount > 1 ? "expenses" : "expense"}
                      </p>
                    </div>
                    <div className="flex justify-end items-center">
                      <div className="flex flex-col items-end">
                        <span className="text-xs font-medium text-gray-500">Total Expense</span>
                        <p className="text-base text-gray-800 font-semibold">
                          ₹{employee.employeeExpense.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center">No employees found for this category</p>
          )}
        </div>
      ) : (
        // 🔹 MAIN VIEW WITH TABS
        <div>
          {/* ✅ TABS - Employee Wise / Category Wise */}
          <div className="flex w-full pb-4 gap-2">
            <button
              onClick={() => handleTabChange("employee")}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === "employee"
                ? "bg-[#2C4E86] text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              Employee Wise
            </button>
            <button
              onClick={() => handleTabChange("category")}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${activeTab === "category"
                ? "bg-[#2C4E86] text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              Category Wise
            </button>
          </div>

          {/* ✅ EMPLOYEE WISE TAB */}
          {activeTab === "employee" && (
            <div>
              {/* Employee Filters */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                    <label className="text-sm text-gray-600 mb-1 font-semibold">Search Employee</label>
                    <input
                      type="text"
                      placeholder="Search by employee name"
                      value={searchFilter}
                      onChange={(e) => {
                        setSearchFilter(e.target.value);
                        setPage(1);
                      }}
                      className="border px-3 py-2 rounded-md"
                    />
                    {searchFilter !== debouncedSearch && (
                      <span className="text-xs text-gray-500 mt-1">Searching...</span>
                    )}
                  </div>

                  <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                    <label className="text-sm text-gray-600 mb-1 font-semibold">From Date</label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        setPage(1);
                      }}
                      className="border px-3 py-2 rounded-md"
                    />
                  </div>

                  <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                    <label className="text-sm text-gray-600 mb-1 font-semibold">To Date</label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                        setPage(1);
                      }}
                      className="border px-3 py-2 rounded-md"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm text-transparent mb-1 font-semibold select-none">Reset</label>
                    <button
                      onClick={handleResetFilters}
                      className="border px-2 py-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-gray-700"
                      title="Clear all filters"
                    >
                      <RotateCcw size={17} />
                      <span className="text-xs">Reset</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Employee List */}
              <div>
                {expenses.map((exp) => {
                  const isOpen = expandedId === exp.employeeId;
                  return (
                    <div
                      key={exp.employeeId}
                      className="bg-gray-50 shadow rounded-lg overflow-hidden transition-all duration-200 hover:shadow-md mb-3"
                    >
                      <div
                        className="grid grid-cols-3 items-center px-4 py-3 cursor-pointer gap-4"
                        onClick={() => setExpandedId(isOpen ? null : exp.employeeId)}
                      >
                        {/* Profile + Name Column */}
                        <div className="flex items-center gap-3">
                          <img
                            src={exp.employeePhoto}
                            alt={"Employee"}
                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                          />
                          <div className="flex flex-col">
                            <label className="text-xs text-gray-500">Employee</label>
                            <p className="font-medium text-gray-800">{exp.employeeName || "Unknown"}</p>
                          </div>
                        </div>

                        {/* Total Expense Column */}
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-xs font-medium text-gray-500">Total Expense</span>
                          <p className="text-base text-gray-800 font-semibold">
                            ₹{exp.employeeExpense.toLocaleString()}
                          </p>
                        </div>

                        {/* Chevron Column */}
                        <div className="flex justify-end items-center">
                          {isOpen ? (
                            <ChevronUp className="text-gray-600" />
                          ) : (
                            <ChevronDown className="text-gray-600" />
                          )}
                        </div>
                      </div>

                      {isOpen && (
                        <div className="px-4 py-3 border-t bg-white text-sm text-gray-700">
                          <p className="font-semibold mb-2">Category Expenses:</p>
                          <div className="flex flex-col gap-2">
                            {exp.category?.length
                              ? exp.category.map((cat, i) => (
                                <div
                                  key={cat.categoryId}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCategoryClick(cat.categoryId, cat.categoryName);
                                  }}
                                  className={`px-3 py-2 rounded-lg text-xs font-medium shadow-sm cursor-pointer hover:opacity-80 transition-opacity flex justify-between items-center ${getCategoryBadgeClass(i)}`}
                                >
                                  <span>{cat.categoryName}</span>
                                  <span className="font-semibold">₹{cat.categoryExpense.toLocaleString()}</span>
                                </div>
                              ))
                              : <span className="text-gray-500">-</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ✅ CATEGORY WISE TAB */}
          {activeTab === "category" && (
            <div>
              {/* Category Summary Filters */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                    <label className="text-sm text-gray-600 mb-1 font-semibold">Search Category</label>
                    <input
                      type="text"
                      placeholder="Search by category name"
                      value={categorySummarySearch}
                      onChange={(e) => {
                        setCategorySummarySearch(e.target.value);
                        setCategorySummaryPage(1);
                      }}
                      className="border px-3 py-2 rounded-md"
                    />
                    {categorySummarySearch !== categorySummaryDebouncedSearch && (
                      <span className="text-xs text-gray-500 mt-1">Searching...</span>
                    )}
                  </div>

                  <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                    <label className="text-sm text-gray-600 mb-1 font-semibold">From Date</label>
                    <input
                      type="date"
                      value={categorySummaryFromDate}
                      onChange={(e) => {
                        setCategorySummaryFromDate(e.target.value);
                        setCategorySummaryPage(1);
                      }}
                      className="border px-3 py-2 rounded-md"
                    />
                  </div>

                  <div className="flex flex-col flex-1 min-w-[180px] max-w-[250px]">
                    <label className="text-sm text-gray-600 mb-1 font-semibold">To Date</label>
                    <input
                      type="date"
                      value={categorySummaryToDate}
                      onChange={(e) => {
                        setCategorySummaryToDate(e.target.value);
                        setCategorySummaryPage(1);
                      }}
                      className="border px-3 py-2 rounded-md"
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm text-transparent mb-1 font-semibold select-none">Reset</label>
                    <button
                      onClick={handleResetCategorySummaryFilters}
                      className="border px-2 py-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-gray-700"
                      title="Clear all filters"
                    >
                      <RotateCcw size={17} />
                      <span className="text-xs">Reset</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Category Summary List */}
              {isCategorySummaryLoading ? (
                <p></p>
              ) : categoryList.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryList.map((category, index) => (
                    <div
                      key={category.categoryId}
                      onClick={() => handleCategoryClick(category.categoryId, category.categoryName)}
                      className="bg-gradient-to-br from-white to-gray-50 shadow-md rounded-lg p-5 border border-gray-200 hover:shadow-lg transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {category.categoryName}
                        </h3>
                        <div className={`w-3 h-3 rounded-full ${getCategoryBadgeClass(index).split(' ')[0]}`}></div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Total Expense:</span>
                          <span className="text-lg font-bold text-gray-900">
                            ₹{category.categoryExpense.toLocaleString()}
                          </span>
                        </div>

                        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                          <span className="text-sm text-gray-600">Employees:</span>
                          <span className="text-sm font-semibold text-gray-700">
                            {category.employeeCount} {category.employeeCount > 1 ? 'people' : 'person'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center">No categories found</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
